import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { of, EMPTY } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  map,
  tap,
  withLatestFrom,
  catchError,
  switchMap,
  filter,
  mergeMap
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { IOrderDTO } from '@care-portals/api-interfaces';
import { APIService } from '../../services/api/api.service';
import * as PrescriptionActions from './prescription.actions';
import * as PrescriptionSelectors from './prescription.selectors';
import * as fromPrescription from './prescription.reducer';
import * as OrdersActions from '../order/order.actions';
import { ListQueryService } from '../../components/common/pagination/list-query.service';
import { listInvalidationEffect } from '../../components/common/pagination/list-query-invalidation.effect';
import { PRESCRIPTION_LIST_QUERY_PREFIX } from './prescription.query-keys';

@Injectable()
export class PrescriptionEffects {
  // A prescription mutation can change what the list page shows, so drop the
  // keyed list caches (the offset/SWR path the list page renders from). The
  // embedded cursor lists are unaffected — they read the NgRx collection.
  invalidateListQueries$ = listInvalidationEffect(
    () => this.actions$,
    () => this.listQuery,
    PRESCRIPTION_LIST_QUERY_PREFIX,
    [
      PrescriptionActions.createPrescriptionSuccess,
      PrescriptionActions.updatePrescriptionSuccess,
      PrescriptionActions.addFulfillmentSuccess,
      PrescriptionActions.sendRxSuccess,
      PrescriptionActions.sendPrescriptionPdfToChatSuccess
    ]
  );
  private readonly notificationTitleMap = {
    info: 'Info',
    warning: 'Warning',
    error: 'Error'
  };

  // Helper method to get notification message and type from reason
  private getNotificationFromReason(
    reason: string,
    orderCreated?: boolean
  ): { message: string; type: 'info' | 'warning' | 'error' } | null {
    const reasonMap: Record<
      string,
      { message: string; type: 'info' | 'warning' | 'error' }
    > = {
      already_fulfilled: {
        message:
          'Prescription is already fulfilled but has been re-sent to pharmacy.',
        type: 'info'
      },
      prescription_not_found: {
        message: 'Prescription not found. Unable to send to pharmacy.',
        type: 'error'
      },
      no_order_found: {
        message:
          'No order found for prescription. Unable to fulfill but sent to pharmacy.',
        type: 'warning'
      },
      no_drugs: {
        message:
          'No valid drugs found for prescription but has been sent to pharmacy.',
        type: 'warning'
      },
      no_products: {
        message:
          'Cannot create order: No products found for prescription drugs but has been sent to pharmacy.',
        type: 'warning'
      },
      order_creation_failed: {
        message:
          'Failed to create order from prescription but has been sent to pharmacy.',
        type: 'error'
      },
      fulfillment_failed: {
        message: orderCreated
          ? 'Order created but prescription fulfillment failed but has been sent to pharmacy.'
          : 'Prescription fulfillment failed but has been sent to pharmacy.',
        type: orderCreated ? 'warning' : 'error'
      }
    };

    return reasonMap[reason] || null;
  }

  // Helper method to show notification based on type
  private showNotification(
    type: 'info' | 'warning' | 'error' | 'success',
    title: string,
    message: string
  ): void {
    switch (type) {
      case 'info':
        this.notification.info(title, message);
        break;
      case 'warning':
        this.notification.warning(title, message);
        break;
      case 'error':
        this.notification.error(title, message);
        break;
      case 'success':
        this.notification.success(title, message);
        break;
    }
  }

  // Helper method to handle sendRx response
  private handleSendRxResponse(response: any): {
    prescription: any;
    orderCreated: boolean;
  } {
    const prescription = response.prescription || response;
    const orderCreated = response.orderCreated || false;
    const alreadyFulfilled = response.alreadyFulfilled || false;
    const reason = response.reason;

    if (reason) {
      const notification = this.getNotificationFromReason(reason, orderCreated);
      if (notification) {
        this.showNotification(
          notification.type,
          this.notificationTitleMap[notification.type],
          notification.message
        );
      }
    } else if (alreadyFulfilled) {
      this.showNotification(
        'info',
        'Prescription Already Fulfilled',
        'This prescription has already been fulfilled but has been re-sent to pharmacy.'
      );
    } else if (orderCreated) {
      this.showNotification(
        'success',
        'Order Created & Rx Sent',
        'Order has been created and prescription has been sent to pharmacy.'
      );
    } else {
      this.showNotification(
        'success',
        'Rx Sent',
        'Prescription has been auto-fulfilled and sent to pharmacy.'
      );
    }

    return { prescription, orderCreated };
  }

  // Helper method to handle error and show notification
  private handleError(error: any, orderCreated: boolean = false): void {
    const errorMessage =
      error?.error?.message || error?.message || 'Unable to send prescription';
    const reason = error?.error?.reason;

    if (reason) {
      const notification = this.getNotificationFromReason(reason, orderCreated);
      if (notification) {
        this.showNotification(
          notification.type,
          this.notificationTitleMap[notification.type],
          notification.message
        );
        return;
      }
    }

    // Fallback to error message if no reason found
    if (errorMessage !== 'Unable to send prescription') {
      this.showNotification('error', 'Error', errorMessage);
    } else {
      this.showNotification('error', 'Error', 'Unable to send prescription');
    }
  }
  loadPrescriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.loadPrescriptions),
      switchMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(PrescriptionSelectors.getPrescriptionState)
          )
        )
      ),
      switchMap(([action, state]) => {
        const filters = { ...action.filters };
        if (action.loadType === 'newer' && state.maxId) {
          Object.assign(filters, { minId: state.maxId });
        } else if (action.loadType === 'older' && state.minId) {
          Object.assign(filters, { maxId: state.minId });
        }

        return this.apiService.getAllPrescriptions(filters).pipe(
          // Cursor calls omit includeCount, so the API returns a bare array;
          // normalise in case a paginated envelope ever comes back.
          map(res => (Array.isArray(res) ? res : res.data)),
          tap(prescriptions => {
            if (!prescriptions.length) {
              if (action.loadType === 'newer') {
                this.notification.info('Info', 'You are all caught up!');
              } else {
                this.notification.info(
                  'Info',
                  'There are no older prescriptions'
                );
              }
            }
          }),
          map(prescriptions =>
            PrescriptionActions.loadPrescriptionsSuccess({
              prescriptions,
              loadType: action.loadType
            })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error loading prescriptions.'
            );
            return of(PrescriptionActions.loadPrescriptionsFailure({ error }));
          })
        );
      })
    )
  );

  fetchPrescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.fetchPrescription),
      switchMap(({ prescriptionId }) =>
        this.apiService.getPrescription(prescriptionId).pipe(
          tap(prescription => {
            if (!prescription) {
              this.notification.info('Info', 'There are no prescription');
            }
          }),
          map(prescription =>
            PrescriptionActions.fetchPrescriptionSuccess({ prescription })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error loading prescription.'
            );
            return of(PrescriptionActions.fetchPrescriptionFailure({ error }));
          })
        )
      )
    )
  );

  createPrescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.createPrescription),
      switchMap(action =>
        this.apiService.createPrescription(action.prescription).pipe(
          tap(() =>
            this.notification.success('Success', 'RX Created Successfully')
          ),
          map(prescription =>
            PrescriptionActions.createPrescriptionSuccess({
              prescription,
              orderId: action.orderId,
              sendRx: action.sendRx
            })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Failed to create RX', error.message);
            return of(PrescriptionActions.createPrescriptionFailure({ error }));
          })
        )
      )
    )
  );

  // Handle side effect chain: sendRx + updateOrder after prescription creation
  createPrescriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.createPrescriptionSuccess),
      filter(action => action.orderId && action.sendRx),
      switchMap(action =>
        this.apiService.sendRx(action.prescription._id).pipe(
          map((response: any) => {
            return this.handleSendRxResponse(response);
          }),
          catchError(error => {
            console.error('Error', error);
            this.handleError(error);
            // Continue with order update even if sendRx fails
            return of(null);
          }),
          switchMap(result => {
            if (action.orderId) {
              return this.apiService
                .updateOrder(action.orderId, {
                  status: 'awaiting_fulfillment'
                })
                .pipe(
                  map(() =>
                    PrescriptionActions.createPrescriptionWithSendRxComplete()
                  ),
                  catchError(error => {
                    console.error('Error updating order', error);
                    return of(
                      PrescriptionActions.createPrescriptionWithSendRxComplete()
                    );
                  })
                );
            }
            return of(
              PrescriptionActions.createPrescriptionWithSendRxComplete()
            );
          })
        )
      )
    )
  );

  updatePrescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.updatePrescription),
      switchMap(action =>
        this.apiService.updatePrescription(action.id, action.data).pipe(
          map(prescription =>
            PrescriptionActions.updatePrescriptionSuccess({
              prescription,
              linkToOrderContext: action.linkToOrderContext
            })
          ),
          catchError(error =>
            of(
              PrescriptionActions.updatePrescriptionFailure({
                error,
                id: action.id
              })
            )
          )
        )
      )
    )
  );

  addFulfillment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.addFulfillment),
      switchMap(action =>
        this.apiService.addFulfillment(action.id, action.fulfillment).pipe(
          tap(() =>
            this.notification.success(
              'Success',
              'Fulfillment Added Successfully'
            )
          ),
          map(prescription =>
            PrescriptionActions.addFulfillmentSuccess({
              prescription,
              orderId: action.fulfillment.orderId
            })
          ),
          catchError(error => {
            const errorMessage =
              (error.status === 400 || error.statusCode === 400) &&
              error.message
                ? error.message
                : 'Failed to add fulfillment';
            this.notification.error('Error', errorMessage);
            return of(PrescriptionActions.addFulfillmentFailure({ error }));
          })
        )
      )
    )
  );

  updatePrescriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.updatePrescriptionSuccess),
      tap(action => {
        const ctx = action.linkToOrderContext;
        if (ctx) {
          const linkedOrderId = action.prescription?.linkedOrderId;
          if (linkedOrderId == null) {
            this.notification.success(
              'Prescription unlinked',
              `RX ${action.prescription._id} is no longer linked to order #${ctx.orderId}.`
            );
          } else if (Number(linkedOrderId) === Number(ctx.orderId)) {
            this.notification.success(
              'Prescription linked',
              `RX ${action.prescription._id} is now linked to order #${ctx.orderId}.`
            );
          } else {
            this.notification.success('Success', 'Prescription Updated');
          }
        } else {
          this.notification.success('Success', 'Prescription Updated');
        }
      }),
      mergeMap(action =>
        action.linkToOrderContext
          ? of(
              OrdersActions.loadOrder({
                orderId: action.linkToOrderContext.orderId
              }),
              ...(action.linkToOrderContext.transferOutAfterLink
                ? [
                    OrdersActions.updateOrder({
                      orderId: action.linkToOrderContext.orderId,
                      data: { transferOut: true } as IOrderDTO
                    })
                  ]
                : [])
            )
          : EMPTY
      )
    )
  );

  updatePrescriptionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PrescriptionActions.updatePrescriptionFailure),
        tap(({ error }) => {
          console.error('Error', error);
          this.showNotification(
            'error',
            'Error',
            'Unable to update prescription. Please try again.'
          );
        })
      ),
    { dispatch: false }
  );

  sendRx$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.sendRx),
      switchMap(action =>
        this.apiService.sendRx(action.rxId).pipe(
          map((response: any) => {
            const { prescription, orderCreated } =
              this.handleSendRxResponse(response);
            return PrescriptionActions.sendRxSuccess({
              prescription,
              orderCreated
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.handleError(error);
            return of(PrescriptionActions.sendRxFailure({ error }));
          })
        )
      )
    )
  );

  downloadPrescriptionPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.downloadPrescriptionPdf),
      switchMap(action =>
        this.apiService.getPrescriptionPdfSignedUrl(action.rxId).pipe(
          map(response => {
            if (response.success && response.signedUrl) {
              return PrescriptionActions.downloadPrescriptionPdfSuccess({
                signedUrl: response.signedUrl
              });
            } else {
              this.notification.error('Error', 'Failed to generate PDF URL');
              return PrescriptionActions.downloadPrescriptionPdfFailure({
                error: 'Failed to generate PDF URL'
              });
            }
          }),
          catchError(error => {
            console.error('Error getting prescription PDF URL:', error);
            this.notification.error(
              'Error',
              'Failed to download PDF. Please try again.'
            );
            return of(
              PrescriptionActions.downloadPrescriptionPdfFailure({
                error: error?.message || 'Unknown error'
              })
            );
          })
        )
      )
    )
  );

  downloadPrescriptionPdfSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PrescriptionActions.downloadPrescriptionPdfSuccess),
        tap(action => {
          window.open(action.signedUrl, '_blank');
        })
      ),
    { dispatch: false }
  );

  sendPrescriptionPdfToChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionActions.sendPrescriptionPdfToChat),
      mergeMap(action =>
        this.apiService.sendPrescriptionPdfToPatientChat(action.rxId).pipe(
          switchMap(() => {
            this.notification.success(
              'Sent',
              'Prescription PDF was sent to the patient chat. RX has been marked as transferred out.'
            );
            const actions = [
              PrescriptionActions.sendPrescriptionPdfToChatSuccess({
                rxId: action.rxId
              }),
              ...(action.orderId
                ? [OrdersActions.loadOrder({ orderId: action.orderId })]
                : [])
            ];
            return of(...actions);
          }),
          catchError(error => {
            this.notification.error(
              'Error',
              error?.error?.message ||
                error?.message ||
                'Failed to send prescription to patient chat.'
            );
            return of(
              PrescriptionActions.sendPrescriptionPdfToChatFailure({
                rxId: action.rxId,
                error
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private store: Store<fromPrescription.PrescriptionPartialState>,
    private listQuery: ListQueryService
  ) {}
}
